import { useCallback, useEffect, useState } from "react";
import { getUserProductsData } from "../API/firebase";
import { UserProductTypes } from "../models/types";

export interface UseUserProducts {
  products: UserProductTypes[];
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

/**
 * Registered devices for the signed-in user.
 *
 * Shared so the device list and its container fetch once between them; they
 * previously issued the same query twice — once to decide the empty state and
 * again inside the grid.
 */
export function useUserProducts(): UseUserProducts {
  const [products, setProducts] = useState<UserProductTypes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let aborted = false;

    setLoading(true);
    setError(null);

    getUserProductsData()
      .then((data) => {
        if (aborted) return;
        setProducts(data);
      })
      .catch((err: unknown) => {
        if (aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [nonce]);

  return { products, loading, error, reload };
}

export default useUserProducts;
